import { Alert, Input, Modal, ModalProps, Switch, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import { SearchCandidate } from '../hooks/useSearchCandidates';
import { CandidateDetail, JobPostingDetail, RecruitmentProcessDetail } from '../types';
import Form from './Form';

interface GiveUpApplicantFormValues {
  comment: string;
  reopen?: boolean;
}

interface GiveUpApplicantModalProps extends ModalProps {
  candidate: CandidateDetail | SearchCandidate;
  jobPostingId: JobPostingDetail['id'];
  onSuccess?: (
    data: AxiosResponse<RecruitmentProcessDetail, unknown>,
    variables: GiveUpApplicantFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const GiveUpApplicantModal = ({
  candidate,
  jobPostingId,
  onSuccess,
  ...props
}: GiveUpApplicantModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: candidate.id,
          job_posting: jobPostingId,
        },
      },
    ],
    {
      select(data) {
        return data.results[0];
      },
    }
  );

  const jobPosting = useAPIQuery(`/job-postings/${jobPostingId}/`);

  const mutation = useAPIMutation(
    async ({ comment, reopen }: GiveUpApplicantFormValues, api) => {
      if (reopen) {
        await api.post(`/job-postings/${jobPostingId}/reopen/`);
      }

      return api.patch(`/recruitment-processes/${recruitmentProcess.data?.id}/`, {
        status: 'GIVEN_UP',
        commentary: `${t('Justificativa')}: ` + comment,
      });
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries(
          `/recruitment-processes/${recruitmentProcess.data?.id}/`
        );
        await queryClient.invalidateQueries('/recruitment-processes/');
        await queryClient.invalidateQueries(`/job-postings/${jobPostingId}/`);
      },
    }
  );

  return (
    <Modal
      okButtonProps={{
        htmlType: 'submit',
        loading: mutation.isLoading,
      }}
      okText={t('Salvar')}
      onOk={() => form.submit()}
      title={
        <Typography.Text>
          <Trans>Deseja marcar</Trans> <strong>{candidate.name}</strong>{' '}
          <Trans>como desistente do processo?</Trans>
        </Typography.Text>
      }
      {...props}
    >
      <Helmet>
        <title>
          {t('Marcar desistente')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Alert
        message={
          <Typography.Text>
            <Typography.Text strong>
              <Trans>Atenção</Trans>:{' '}
            </Typography.Text>
            <Trans>
              Ao marcar uma pessoa como desistente do processo a ação não poderá ser desfeita.
            </Trans>
          </Typography.Text>
        }
        type="warning"
        style={{ marginBottom: '2%' }}
      />
      <Form<GiveUpApplicantFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          comment: '',
          reopen: false,
        }}
        onFinish={(variables) => mutation.mutate(variables)}
      >
        <Form.Item
          label={t('Registre um comentário com o motivo da desistência:')}
          name="comment"
          rules={[{ required: true, message: t('Por favor, insira o Comentário') }]}
        >
          <Input.TextArea autoSize={{ minRows: 4, maxRows: 8 }} />
        </Form.Item>
        <Form.Item name="reopen" valuePropName="checked" noStyle>
          <Switch
            disabled={jobPosting.data?.status !== 'FINALIZED'}
            onChange={(checked) => {
              form.setFieldsValue({ reopen: checked });
            }}
          />{' '}
          <Trans>Reabrir vaga</Trans>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GiveUpApplicantModal;
